import { useEffect } from "react";
import { withNamespaces } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import ProcessingActivity from "./processing-activity";
import OverviewHeader from "../../../overview-header";
import Knob from "src/modules/data-protection/pages/admin/dashboard/components/Knob";
import AIGovernanceAssessments from "./ai-governance";
import PrivacyAssessments from "./privacy";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import useAssessmentCards from "./hooks/useAssessmentCards";
import {
    ASSESSMENT_TABS,
    findAssessmentCard,
    getAssessmentCardPercent,
    getAssessmentCardProgressColor,
    getAssessmentCardStatusLabel,
    getAssessmentCardStatusTone,
    getAssessmentTabSlugs,
    normalizeAssessmentCards
} from "./helpers/assessmentCards";

import "./style.scss";

/**
 * `slug` maps each card to its item in the assessment-cards payload, which
 * owns the label, status, completion and visibility of the card.
 */
const DEFAULT_ASSESSMENT = "processing-activity";

const Assessments = ({ t, softwareData, refetchSoftwareData }) => {
    const history = useHistory();
    const location = useLocation();
    /**
     * Comes from the asset details the details page already fetched, so no
     * assessment has to request them again to know whether DPIA is unlocked.
     */
    const impactAssessmentAvailable = !!softwareData?.show_impact_assessment;
    // Preserve the legacy CE Conformity visibility rules: the organisation
    // needs the feature and the assessment must apply to this asset.
    const ceConformityAvailable =
        isFeatureEnabledForActiveModule("ce-conformity") &&
        !!softwareData?.provider_conformity_required;

    const {
        data: assessmentCards,
        isLoading: assessmentCardsAreLoading,
        refetch: refetchAssessmentCards
    } = useAssessmentCards(softwareData?.id, { t });

    const cardItems = normalizeAssessmentCards(assessmentCards);

    const getCard = (assessment) => {
        return findAssessmentCard(
            cardItems,
            getAssessmentTabSlugs(assessment, softwareData?.ai_setup_mode)
        );
    };

    const visibleAssessmentTabs = ASSESSMENT_TABS.filter((assessment) => {
        if (assessment.key === "ce-conformity" && !ceConformityAvailable) {
            return false;
        }

        const card = getCard(assessment);

        if (!card) {
            return !assessmentCards && assessment.key !== "ce-conformity";
        }

        return card.visible !== false;
    });
    const searchParams = new URLSearchParams(location.search);
    const requestedAssessment =
        searchParams.get("assessment") || DEFAULT_ASSESSMENT;
    // The DPIA questionnaire is unlocked by the risk assessment result.
    const activeAssessment =
        requestedAssessment === "dpia" && !impactAssessmentAvailable
            ? "risk-assessment"
            : visibleAssessmentTabs.some(
                (assessment) => assessment.key === requestedAssessment
            )
            ? requestedAssessment
            : DEFAULT_ASSESSMENT;

    /**
     * Name the default card in the URL so the selection is shareable. Guarded
     * on the active tab, because this still runs while navigating away and
     * would otherwise pull the user back into Assessments.
     */
    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (params.get("tab") !== "assessments" || params.get("assessment")) {
            return;
        }

        history.replace({
            search: `?tab=assessments&assessment=${DEFAULT_ASSESSMENT}`
        });
    }, [history, location.search]);

    const isAssessmentDisabled = (assessment) => {
        return assessment.key === "dpia" && !impactAssessmentAvailable;
    };

    const handleAssessmentClick = (assessment) => {
        if (isAssessmentDisabled(assessment)) {
            return;
        }

        history.push({
            search: `?tab=assessments&assessment=${assessment.key}`
        });
    };

    return (
        <div className="assessments-tab p-4">
            <OverviewHeader assetId={softwareData.id} />

            <div className="mt-3 mb-3">
                <h4 className="mb-1">{t("Assessments")}</h4>
                <p className="text-muted mb-0">
                    {t(
                        "All compliance assessments for this asset, organised by domain."
                    )}
                </p>
            </div>

            <div
                className="assessments-tab__navigation"
                role="tablist"
                aria-label={t("Assessments")}
                aria-busy={assessmentCardsAreLoading}
            >
                {assessmentCardsAreLoading
                    ? visibleAssessmentTabs.map((assessment) => (
                        <div
                            key={assessment.key}
                            className={`assessments-tab__navigation-item ${
                                assessment.key === activeAssessment
                                    ? "active"
                                    : ""
                            }`}
                            aria-hidden="true"
                        >
                            <span className="assessments-tab__navigation-main">
                                <span
                                    className="dt-field dt-skeleton mb-2"
                                    style={{ height: "16px", width: "70%" }}
                                />
                                <span
                                    className="dt-field dt-skeleton"
                                    style={{ height: "12px", width: "40%" }}
                                />
                            </span>
                        </div>
                    ))
                    : visibleAssessmentTabs.map((assessment) => {
                    const isActive = assessment.key === activeAssessment;
                    const isDisabled = isAssessmentDisabled(assessment);
                    const card = getCard(assessment);
                    const percent = getAssessmentCardPercent(card);
                    const statusTone = getAssessmentCardStatusTone(card);

                    return (
                        <button
                            key={assessment.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            disabled={isDisabled}
                            title={
                                isDisabled
                                    ? t("Available for high risk assets")
                                    : undefined
                            }
                            className={`assessments-tab__navigation-item ${
                                isActive ? "active" : ""
                            } ${isDisabled ? "disabled" : ""}`}
                            onClick={() =>
                                handleAssessmentClick(assessment)
                            }
                        >
                            <span className="assessments-tab__navigation-main">
                                <span className="assessments-tab__navigation-title">
                                    {t(card?.label || assessment.title)}
                                </span>
                                <span
                                    className={`assessments-tab__navigation-status assessments-tab__navigation-status--${
                                        isDisabled
                                            ? "not-available"
                                            : statusTone
                                    }`}
                                >
                                    {isDisabled
                                        ? t("Not Available")
                                        : t(
                                            getAssessmentCardStatusLabel(card)
                                        )}
                                </span>
                            </span>
                            <span className="assessments-tab__progress">
                                <Knob
                                    value={percent}
                                    lineCap="round"
                                    height={42}
                                    width={42}
                                    fgColor={
                                        isDisabled
                                            ? "#6C757E"
                                            : getAssessmentCardProgressColor(
                                                card
                                            )
                                    }
                                    thickness={0.28}
                                    readOnly
                                />
                            </span>
                        </button>
                    );
                })}
            </div>

            <div role="tabpanel">
                {["eu-ai-act", "fria", "ce-conformity"].includes(activeAssessment) ? (
                    <AIGovernanceAssessments
                        assetId={softwareData.id}
                        softwareData={softwareData}
                        refetchSoftwareData={refetchSoftwareData}
                        activeAssessment={activeAssessment}
                        onAssessmentSaved={refetchAssessmentCards}
                    />
                ) : ["risk-assessment", "dpia"].includes(activeAssessment) ? (
                    <PrivacyAssessments
                        // Remount so the reported progress belongs to the
                        // questionnaire currently on screen.
                        key={activeAssessment}
                        assetId={softwareData.id}
                        softwareData={softwareData}
                        refetchSoftwareData={refetchSoftwareData}
                        activeAssessment={activeAssessment}
                        onAssessmentSaved={refetchAssessmentCards}
                    />
                ) : (
                    <ProcessingActivity
                        softwareData={softwareData}
                        onAssessmentSaved={refetchAssessmentCards}
                    />
                )}
            </div>
        </div>
    );
};

export default withNamespaces()(Assessments);
